import { Fragment, isValidElement, useId, useRef, type ReactNode } from 'react';
import {
  Body,
  Button,
  Dialog,
  DialogFooter,
  DialogSection,
  Spinner,
  Stack,
  type DialogProps,
} from '@leanin/ui';

// Counts renderable child nodes, descending through empty Fragments + arrays
// so `<></>` and `<><></><></></>` both count as 0. Used to detect whether
// the dialog body has any actual content before rendering its band chrome.
function countRenderableChildren(node: ReactNode): number {
  if (node === null || node === undefined || typeof node === 'boolean') return 0;
  if (Array.isArray(node)) {
    let n = 0;
    for (const item of node) n += countRenderableChildren(item);
    return n;
  }
  if (isValidElement(node) && node.type === Fragment) {
    const props = node.props as { children?: ReactNode };
    return countRenderableChildren(props.children);
  }
  // string / number / non-Fragment element — counts as one.
  return 1;
}

export type FormModalProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  titleTone?: DialogProps['titleTone'];
  headerDivider?: DialogProps['headerDivider'];
  description?: string;
  children: ReactNode;
  error?: string | null;
  isSubmitting?: boolean;
  onSubmit: () => void | Promise<void>;
  primaryLabel: string;
  primaryVariant?: 'primary' | 'secondary' | 'ghost' | 'ghostDanger';
  primaryDisabled?: boolean;
  secondaryLabel?: string | null;
  onSecondary?: () => void;
};

export function FormModal({
  open,
  onOpenChange,
  title,
  titleTone,
  headerDivider,
  description,
  children,
  error,
  isSubmitting,
  onSubmit,
  primaryLabel,
  primaryVariant = 'primary',
  primaryDisabled,
  secondaryLabel = 'Cancel',
  onSecondary,
}: FormModalProps) {
  const showSecondary = secondaryLabel !== null;
  const formId = useId();
  // Dismissal guard for the whole submit flight. The ref is set
  // synchronously in the click/submit handler and covers the window before
  // the `isSubmitting` render commits (an Escape can land inside it and
  // would read the stale not-pending value); `isSubmitting` covers the rest
  // of an async flight for consumers whose onSubmit returns void and drives
  // a mutation. Dismissing mid-flight resets consumers' forms and hides
  // their errors, so it is refused rather than raced.
  const submitInFlightRef = useRef(false);
  const runSubmit = (): void => {
    submitInFlightRef.current = true;
    void Promise.resolve()
      .then(() => onSubmit())
      .finally(() => {
        submitInFlightRef.current = false;
      });
  };
  // Acknowledgement-style usages (e.g. "Thanks for letting us know" after a
  // report submission) pass an empty fragment as `children`. Skipping the
  // body section in that case keeps the dialog from rendering an empty band
  // with two dividers between the description and the footer button.
  //
  // `React.Children.count(<></>)` returns 1 (the Fragment itself is a child),
  // so we recursively descend through Fragments + arrays to count *actual*
  // renderable nodes before deciding whether to render the body band.
  const hasBody =
    countRenderableChildren(children) > 0 ||
    (error !== undefined && error !== null && error !== '');
  return (
    <Dialog
      open={open}
      onOpenChange={onOpenChange}
      canDismiss={() => !submitInFlightRef.current && isSubmitting !== true}
      title={title}
      titleTone={titleTone}
      headerDivider={headerDivider}
      description={description}
    >
      {/* The form lives inside the scrollable body section; the submit button
       *  in the footer is associated by `form={formId}` so the footer can stay
       *  docked as a direct Dialog child (Enter-to-submit still works because
       *  the inputs are inside the form). Wrapping Section + Footer in a
       *  `<form>` would force Dialog's `intoBands` to wrap them together in an
       *  auto-section — double padding, no scroll, footer unpinned. */}
      {hasBody ? (
        <DialogSection scrollable>
          <form
            id={formId}
            onSubmit={(e) => {
              e.preventDefault();
              runSubmit();
            }}
          >
            <Stack gap="lg">
              {children}
              {error ? (
                <Body color="primary">
                  {error}
                </Body>
              ) : null}
            </Stack>
          </form>
        </DialogSection>
      ) : null}
      <DialogFooter>
        {showSecondary ? (
          <Button
            variant="ghost"
            type="button"
            onClick={onSecondary ?? (() => onOpenChange(false))}
            disabled={isSubmitting}
          >
            {secondaryLabel ?? 'Cancel'}
          </Button>
        ) : null}
        <Button
          variant={primaryVariant}
          type={hasBody ? 'submit' : 'button'}
          {...(hasBody ? { form: formId } : { onClick: runSubmit })}
          disabled={primaryDisabled || isSubmitting}
          icon={isSubmitting ? <Spinner size="sm" /> : undefined}
        >
          {primaryLabel}
        </Button>
      </DialogFooter>
    </Dialog>
  );
}
